/* ============================================================
   Product analytics — Google Analytics (GA4) through gtag.js, the web twin of
   app/src/analytics.ts. Same property as the iOS app and habishine.com, on its
   own web data stream for app.habishine.com (docs/analytics.md → Web app).

   THIS IS THE ONLY FILE THAT TOUCHES gtag. Callers get `track`, `screen` and
   `identify`, never the SDK — the same containment as src/sentry.ts, so a
   vendor swap touches one file. The core's `login` / `habit_checkin` /
   `task_completed` arrive through the analytics adapter (platform/index.ts).

   NO CONSENT BANNER, UNLIKE THE MARKETING SITE — and that is a decision, not
   an omission. habishine.com is an anonymous public page, so its gtag waits
   for a click on Accept. This is the signed-in app: the privacy policy already
   discloses usage analytics keyed on the account id for "the app", the events
   are the ones the phone sends, and the page sits behind an account that
   accepted that policy at sign-up. The cookie GA sets here is first-party and
   scoped to app.habishine.com. Google signals and ad personalisation are off,
   as everywhere else (that is what keeps "we do not track you across other
   apps or websites" in legal.ts true).

   NEVER PASS USER TEXT. Task titles, habit names, project names and emails
   are somebody's personal list; they must not reach Google. Params are enums,
   booleans and counts only — the `Params` type enforces the shape, each
   caller is responsible for the meaning. Screen names are route NAMES, never
   ids: `/project/<uuid>` reports as `project`.

   OFF WITHOUT A MEASUREMENT ID, OFF IN DEV. `VITE_GA_MEASUREMENT_ID` is
   deploy-time config written by desktop/deploy.sh (public, like the DSN);
   absent = nothing from Google loads at all. In `npm run dev` collection is
   off (the Sentry rule — don't pollute real numbers with your own clicks)
   unless `VITE_ANALYTICS_DEBUG=1`, which also sets `debug_mode` so the
   session shows up in GA4 → Admin → DebugView.

   NEVER BREAKS A FLOW. Every call is fire-and-forget and swallows its own
   failure: a lost event costs a data point, a throw here would cost the
   sign-up or purchase it was measuring.
   ============================================================ */

/** Every event this client sends — a union, not `string`, so the event
   dictionary in docs/analytics.md stays the whole list and a typo fails
   `tsc`. A subset of the phone's: no notification prompt, no restore, no
   in-app account deletion on the web yet. */
export type AnalyticsEvent =
  | 'sign_up'
  | 'login'
  | 'paywall_view'
  | 'begin_checkout'
  | 'trial_started'
  | 'subscription_purchased'
  | 'purchase_cancelled'
  | 'habit_created'
  | 'task_created'
  | 'habit_checkin'
  | 'task_completed';

export type Params = Record<string, string | number | boolean>;

export interface Analytics {
  track(event: AnalyticsEvent, params?: Params): void;
  /** A route or step the person is looking at. Sent as GA4's web `page_view`
     with `page_title` = the name, which is the same dimension ("Page title
     and screen class") the phone's `screen_view` lands in — so `today` on an
     iPhone and `today` in a browser are one row in the Pages and screens
     report. Consecutive repeats are collapsed. */
  screen(name: string): void;
  /** Ties events to the Cognito `sub` (never the email), so one person on two
     devices counts once. `null` on sign-out. */
  identify(sub: string | null): void;
}

/** What a gtag.js stub is: push the arguments onto the data layer and let the
   script, once loaded, replay them. Typed loosely on purpose — the gtag
   command vocabulary is open-ended. */
type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

const ID_SHAPE = /^G-[A-Z0-9]+$/;

/** Builds the three calls over a gtag function. Exported for the tests, which
   hand in a recording stub; the module's own instance below is what the app
   uses. `enabled: false` yields a no-op whose calls cost nothing and reach
   nothing. */
export function createAnalytics(gtag: Gtag | null, enabled: boolean): Analytics {
  if (!enabled || !gtag) {
    return { track() {}, screen() {}, identify() {} };
  }
  let lastScreen: string | null = null;
  const safely = (run: () => void) => {
    try {
      run();
    } catch {
      // ignored — see the header
    }
  };
  return {
    track(event, params) {
      safely(() => gtag('event', event, params ?? {}));
    },
    screen(name) {
      if (name === lastScreen) return;
      lastScreen = name;
      safely(() =>
        gtag('event', 'page_view', {
          page_title: name,
          // the path, never the full URL: a query string or hash could carry
          // anything, and the route is all a report needs
          page_location: `${location.origin}${location.pathname}`,
          page_path: location.pathname,
        }),
      );
    },
    identify(sub) {
      safely(() => gtag('set', { user_id: sub }));
    },
  };
}

/** Puts the gtag stub + data layer on `window` and starts gtag.js loading.
   Mirrors website/site/analytics.js `load()`, minus the consent gate. Never
   sends an automatic page view: the router reports screens itself (an SPA's
   URL changes without a load, and the names must match the phone's). */
function installGtag(id: string, debug: boolean): Gtag {
  window.dataLayer = window.dataLayer || [];
  const gtag: Gtag = function () {
    // gtag.js reads `arguments`, not an array — spreading it would break replay
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', id, {
    send_page_view: false,
    // No Google signals, no ad personalisation — the same stance as the app
    // and the site; see the header.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...(debug ? { debug_mode: true } : {}),
  });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
  return gtag;
}

const MEASUREMENT_ID: string = import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';
const DEBUG = import.meta.env.VITE_ANALYTICS_DEBUG === '1';

export const analyticsEnabled: boolean =
  ID_SHAPE.test(MEASUREMENT_ID) && (!import.meta.env.DEV || DEBUG) && typeof document !== 'undefined';

const instance = createAnalytics(analyticsEnabled ? installGtag(MEASUREMENT_ID, DEBUG) : null, analyticsEnabled);

export const track: Analytics['track'] = (event, params) => instance.track(event, params);
export const screen: Analytics['screen'] = (name) => instance.screen(name);
export const identify: Analytics['identify'] = (sub) => instance.identify(sub);
